import { useState, useCallback } from 'react';
import { Node, Edge } from 'reactflow';

interface WorkflowSnapshot {
  nodes: Node[];
  edges: Edge[];
  timestamp: number;
  action: string;
}

interface WorkflowHistoryState {
  past: WorkflowSnapshot[];
  present: WorkflowSnapshot | null;
  future: WorkflowSnapshot[];
}

export const useWorkflowHistory = () => {
  const [history, setHistory] = useState<WorkflowHistoryState>({
    past: [],
    present: null,
    future: []
  });

  const pushState = useCallback((nodes: Node[], edges: Edge[], action: string) => {
    const snapshot: WorkflowSnapshot = {
      nodes: JSON.parse(JSON.stringify(nodes)), // Deep copy
      edges: JSON.parse(JSON.stringify(edges)), // Deep copy
      timestamp: Date.now(),
      action
    };

    setHistory(prev => ({
      past: prev.present ? [...prev.past, prev.present] : prev.past,
      present: snapshot,
      future: [] // Clear future on new action
    }));
  }, []);

  const undo = useCallback(() => {
    setHistory(prev => {
      if (prev.past.length === 0) return prev;
      
      const previous = prev.past[prev.past.length - 1];
      const newPast = prev.past.slice(0, -1);
      
      return {
        past: newPast,
        present: previous,
        future: prev.present ? [prev.present, ...prev.future] : prev.future
      };
    });
  }, []);

  const redo = useCallback(() => {
    setHistory(prev => {
      if (prev.future.length === 0) return prev;
      
      const next = prev.future[0];
      const newFuture = prev.future.slice(1);
      
      return {
        past: prev.present ? [...prev.past, prev.present] : prev.past,
        present: next,
        future: newFuture
      };
    });
  }, []);

  const canUndo = history.past.length > 0;
  const canRedo = history.future.length > 0;
  const currentState = history.present;

  return { 
    pushState, 
    undo, 
    redo, 
    canUndo, 
    canRedo, 
    currentState 
  };
};
